const STORAGE_SEED = 'apc-ticker-seed';
const STORAGE_CURSOR = 'apc-ticker-cursor';
const POOL_VERSION = 2;
const STORAGE_POOL_VERSION = 'apc-ticker-pool-v';
const POOL_SIZE = 1200;
const DEFAULT_WINDOW = 28;

const NAMES = [
    'Mei',
    'Arif',
    'Linh',
    'Kenji',
    'Sara',
    'Omar',
    'Pim',
    'Dewi',
    'Alex',
    'Nina',
    'Marco',
    'Yuki',
    'Hana',
    'Rizal',
    'Elif',
    'Tuan',
    'Aria',
    'Zara',
    'Leo',
    'Priya',
    'Minh',
    'Sofia',
    'Noah',
    'Amira',
    'Jonas',
    'Chloe',
    'Wei',
    'Fatima',
    'Lucas',
    'Anya',
    'Ethan',
    'Maya',
    'Diego',
    'Ines',
    'Hugo',
    'Layla',
    'Viktor',
    'Emma',
    'Kai',
    'Nadia',
    'Felix',
    'Suki',
    'Raj',
    'Olivia',
    'Theo',
    'Camila',
    'Ivan',
    'Zoe',
    'Adam',
    'Luna',
];

const CHAINS = ['Solana', 'TON', 'Tron', 'Base', 'Ethereum', 'Polygon'];

const VOUCHER_SLUGS = [
    'gaming',
    'streaming',
    'shopping',
    'food',
    'music',
    'travel',
    'mobile',
    'ecommerce',
    'sports',
    'education',
];

const VOUCHER_LABELS = {
    en: {
        gaming: 'Gaming',
        streaming: 'Streaming',
        shopping: 'Shopping',
        food: 'Food delivery',
        music: 'Music',
        travel: 'Travel',
        mobile: 'Mobile top-up',
        ecommerce: 'E-commerce',
        sports: 'Sports',
        education: 'Education',
    },
    vi: {
        gaming: 'Game',
        streaming: 'Streaming',
        shopping: 'Mua sắm',
        food: 'Đồ ăn',
        music: 'Nhạc',
        travel: 'Du lịch',
        mobile: 'Nạp điện thoại',
        ecommerce: 'Thương mại điện tử',
        sports: 'Thể thao',
        education: 'Giáo dục',
    },
};

const ACTION_LABELS = {
    en: {
        deposit: 'Deposit',
        voucher: 'Buy Voucher',
        redpacket: 'Claim Red Packet',
    },
    vi: {
        deposit: 'Nạp tiền',
        voucher: 'Mua voucher',
        redpacket: 'Nhận Red Packet',
    },
};

function langKey(lang) {
    return lang === 'vi' ? 'vi' : 'en';
}

function mulberry32(seed) {
    let s = seed >>> 0;
    return () => {
        s += 0x6d2b79f5;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function readStoredNumber(key, fallback) {
    try {
        const raw = window.localStorage.getItem(key);
        if (raw == null) return fallback;
        const n = Number.parseInt(raw, 10);
        return Number.isFinite(n) ? n : fallback;
    } catch {
        return fallback;
    }
}

function writeStoredNumber(key, value) {
    try {
        window.localStorage.setItem(key, String(value));
    } catch {
        /* ignore quota / private mode */
    }
}

function ensureSeed() {
    let seed = readStoredNumber(STORAGE_SEED, 0);
    if (!seed) {
        seed = Math.floor(Math.random() * 2147483647) || 1;
        writeStoredNumber(STORAGE_SEED, seed);
    }
    return seed;
}

function resetPoolIfVersionMismatch() {
    try {
        const v = Number.parseInt(window.localStorage.getItem(STORAGE_POOL_VERSION) || '', 10);
        if (v !== POOL_VERSION) {
            writeStoredNumber(STORAGE_CURSOR, 0);
            window.localStorage.setItem(STORAGE_POOL_VERSION, String(POOL_VERSION));
        }
    } catch {
        /* ignore */
    }
}

function pickKind(rng) {
    const r = rng();
    if (r < 0.6) return 'deposit';
    if (r < 0.9) return 'voucher';
    return 'redpacket';
}

function money(rng, min, max) {
    return (rng() * (max - min) + min).toFixed(2);
}

function pick(list, rng) {
    return list[Math.floor(rng() * list.length)];
}

/** @typedef {{ id: number, name: string, kind: 'deposit'|'voucher'|'redpacket', chain?: string, voucher?: string, amount: string }} TickerRow */

/** @returns {TickerRow[]} */
function buildNeutralPool(seed) {
    const rng = mulberry32(seed);
    /** @type {TickerRow[]} */
    const pool = [];

    for (let id = 0; id < POOL_SIZE; id += 1) {
        const name = pick(NAMES, rng);
        const kind = pickKind(rng);

        if (kind === 'deposit') {
            pool.push({
                id,
                name,
                kind,
                chain: pick(CHAINS, rng),
                amount: `+${money(rng, 50, 520)}`,
            });
        } else if (kind === 'voucher') {
            pool.push({
                id,
                name,
                kind,
                voucher: pick(VOUCHER_SLUGS, rng),
                amount: money(rng, 12, 120),
            });
        } else {
            pool.push({
                id,
                name,
                kind,
                amount: `+${money(rng, 3, 88)}`,
            });
        }
    }

    return pool;
}

let cachedPool = null;
let cachedSeed = 0;

function getPool() {
    resetPoolIfVersionMismatch();
    const seed = ensureSeed();
    if (!cachedPool || cachedSeed !== seed) {
        cachedPool = buildNeutralPool(seed);
        cachedSeed = seed;
    }
    return cachedPool;
}

function formatRow(row, lang) {
    const lk = langKey(lang);
    const actionBase = ACTION_LABELS[lk][row.kind];
    let action = actionBase;

    if (row.kind === 'deposit' && row.chain) {
        action =
            lk === 'vi'
                ? `${actionBase} · USDT trên ${row.chain}`
                : `${actionBase} · USDT on ${row.chain}`;
    } else if (row.kind === 'voucher' && row.voucher) {
        const label = VOUCHER_LABELS[lk][row.voucher] || VOUCHER_LABELS.en[row.voucher];
        action = lk === 'vi' ? `${actionBase} · ${label}` : `${actionBase} · ${label}`;
    }

    return {
        id: row.id,
        name: row.name,
        action,
        amount: row.amount,
    };
}

function readCursor(pool) {
    let cursor = readStoredNumber(STORAGE_CURSOR, 0);
    return ((cursor % pool.length) + pool.length) % pool.length;
}

/**
 * Xem cửa sổ ticker tại vị trí đã lưu (không tăng con trỏ).
 * @returns {{ name: string, action: string, amount: string, id: number }[]}
 */
export function peekTickerItems(lang = 'en', count = DEFAULT_WINDOW) {
    const pool = getPool();
    const cursor = readCursor(pool);

    const items = [];
    for (let i = 0; i < count; i += 1) {
        const row = pool[(cursor + i) % pool.length];
        items.push(formatRow(row, lang));
    }
    return items;
}

/** Tăng con trỏ sau khi user rời trang — lần mở sau tiếp tục từ đây. */
export function advanceTickerCursor(count = DEFAULT_WINDOW) {
    const pool = getPool();
    const cursor = readCursor(pool);
    writeStoredNumber(STORAGE_CURSOR, (cursor + count) % pool.length);
}

/** @deprecated dùng peekTickerItems */
export function takeTickerItems(lang = 'en', count = DEFAULT_WINDOW) {
    return peekTickerItems(lang, count);
}

/** @deprecated */
export function generateTickerItems(lang = 'en', count = DEFAULT_WINDOW) {
    return peekTickerItems(lang, count);
}
